import {
  encodeExtractedContext,
  encodeExtractedContextV2,
  encodeSelectionContextV3,
  mentionType,
} from '@aline/common';
import type {
  MentionUrlAlineAi,
  MentionUrlDocument,
  MentionUrlEmail,
  MentionUrlIntakeForm,
  MentionUrlParty,
  MentionUrlPlaybook,
  MentionUrlReport,
  MentionUrlReportColumn,
  MentionUrlSelection,
  MentionUrlSelectionV2,
  MentionUrlSelectionV3,
  MentionUrlTask,
  MentionUrlUser,
  MentionUrlWorkflow,
} from '@aline/common';
import { MarkdownPlugin } from '@platejs/markdown';
import type { Descendant, TElement } from 'platejs';
import { ElementApi } from 'platejs';
import remarkGfm from 'remark-gfm';
import remarkMdx from 'remark-mdx';

import type { TMentionElement } from './ChatEditorMention/utils';
import { isMentionElement, transformMentionLinks } from './ChatEditorMention/utils';
import type { EditorWithMarkdownApi } from './SelectionMentionPastePlugin';
import { tableMarkdownRule } from './table-markdown-rule';

/**
 * Configured MarkdownPlugin with support for:
 * - GitHub Flavored Markdown (strikethrough, tables)
 * - MDX (underline via <u> tags)
 * - Custom mention serialization
 */
export const ChatEditorMarkdownPlugin = MarkdownPlugin.configure({
  options: {
    /**
     * Enable GFM (GitHub Flavored Markdown) for strikethrough (~~text~~) and tables
     * Enable MDX for underline (<u>) and other HTML-like elements
     */
    remarkPlugins: [remarkGfm, remarkMdx],
    // Custom serialization rules
    rules: {
      // mention_input is the temporary @ trigger element - serialize as empty
      mention_input: {
        serialize: () => ({ type: 'text', value: '' }),
      },
      [mentionType]: {
        serialize: node => {
          // Use type guard to safely narrow to TMentionElement
          if (!isMentionElement(node, mentionType)) return { type: 'text', value: '' };
          return serializeMentionToMarkdown(node);
        },
      },
      table: tableMarkdownRule,
    },
  },
});

/**
 * Deserializes markdown and automatically transforms mention links to mention elements.
 * This wraps editor.api.markdown.deserialize() with automatic transformMentionLinks.
 *
 * Use this instead of calling editor.api.markdown.deserialize() directly when working
 * with markdown that may contain mention links.
 *
 * @param editor - Editor instance with markdown API
 * @param markdown - Markdown string to deserialize
 * @returns Array of Plate elements with mention links transformed
 *
 * @example
 * ```typescript
 * // Instead of:
 * const nodes = editor.api.markdown.deserialize(markdown);
 *
 * // Use:
 * editor.tf.setValue(deserializeMarkdownWithMentions(editor, markdown));
 * ```
 */
export function deserializeMarkdownWithMentions(
  editor: EditorWithMarkdownApi,
  markdown: string
): TElement[] {
  // Explicit: keep Plate's empty-paragraph `\u200B` round-trip (on unless set `false`).
  const deserialized = editor.api.markdown.deserialize(markdown, {
    preserveEmptyParagraphs: true,
  });
  const nodes = deserialized
    .map(node => transformMentionLinks(node))
    .filter((node): node is TElement => ElementApi.isElement(node));
  return nodes.length ? nodes : [{ children: [{ text: '' }], type: 'p' }];
}

/**
 * Serializes a mention element to mdast link node structure for markdown export.
 * Used by MarkdownPlugin serializer in both ChatPanelInput and ChatMessageStaticRenderer.
 *
 * @param mentionNode - The mention element to serialize
 * @returns mdast link node with mention URL and @name text
 *
 * @example
 * ```typescript
 * serialize: (node) => {
 *   if (!isMentionElement(node, mentionType)) return { type: 'text', value: '' };
 *   return serializeMentionToMarkdown(node);
 * }
 * ```
 */

export function serializeMentionToMarkdown(mentionNode: TMentionElement): {
  children: Array<{ type: string; value: string }>;
  type: string;
  url: string;
} {
  switch (mentionNode.mentionTypeName) {
    case 'aline-ai': {
      return {
        children: [{ type: 'text', value: '@Aline AI' }],
        type: 'link',
        url: 'mention:aline-ai' satisfies MentionUrlAlineAi,
      };
    }
    case 'report-column': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.columnName}` }],
        type: 'link',
        url: `mention:report-column/${mentionNode.columnId}` satisfies MentionUrlReportColumn,
      };
    }
    case 'selection': {
      const encoded = encodeExtractedContext(mentionNode.extractedContext);
      return {
        children: [{ type: 'text', value: `@${mentionNode.documentName}` }],
        type: 'link',
        url: `mention:selection/${mentionNode.documentId}?ctx=${encoded}` satisfies MentionUrlSelection,
      };
    }
    case 'selection-v2': {
      const encoded = encodeExtractedContextV2(mentionNode.extractedContext);
      return {
        children: [{ type: 'text', value: `@${mentionNode.documentName}` }],
        type: 'link',
        url: `mention:selection-v2/${mentionNode.documentId}?ctx=${encoded}` satisfies MentionUrlSelectionV2,
      };
    }
    case 'selection-v3': {
      const encoded = encodeSelectionContextV3({
        documentId: mentionNode.documentId,
        focusSuggestionId: mentionNode.focusSuggestionId,
        html: mentionNode.html,
      });
      return {
        children: [{ type: 'text', value: `@${mentionNode.documentName}` }],
        type: 'link',
        url: `mention:selection-v3/${mentionNode.documentId}?ctx=${encoded}` satisfies MentionUrlSelectionV3,
      };
    }
    case 'document': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.documentName}` }],
        type: 'link',
        url: `mention:document/${mentionNode.documentId}` satisfies MentionUrlDocument,
      };
    }
    case 'party': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.partyName}` }],
        type: 'link',
        url: `mention:party/${mentionNode.partyId}` satisfies MentionUrlParty,
      };
    }
    case 'workflow': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.workflowName}` }],
        type: 'link',
        url: `mention:workflow/${mentionNode.workflowId}` satisfies MentionUrlWorkflow,
      };
    }
    case 'report': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.reportName}` }],
        type: 'link',
        url: `mention:report/${mentionNode.reportId}` satisfies MentionUrlReport,
      };
    }
    case 'task': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.taskName}` }],
        type: 'link',
        url: `mention:task/${mentionNode.taskId}` satisfies MentionUrlTask,
      };
    }
    case 'user': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.userName}` }],
        type: 'link',
        url: `mention:user/${mentionNode.userId}` satisfies MentionUrlUser,
      };
    }
    case 'email': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.email}` }],
        type: 'link',
        url: `mention:email/${mentionNode.email}` satisfies MentionUrlEmail,
      };
    }
    case 'intake-form': {
      return {
        children: [{ type: 'text', value: '@Intake Form' }],
        type: 'link',
        url: 'mention:intake-form/current' satisfies MentionUrlIntakeForm,
      };
    }
    case 'playbook': {
      return {
        children: [{ type: 'text', value: `@${mentionNode.playbookName}` }],
        type: 'link',
        url: `mention:playbook/${mentionNode.playbookId}` satisfies MentionUrlPlaybook,
      };
    }
  }
}

export function getMarkdownHasMentionType(
  editor: EditorWithMarkdownApi,
  markdown: string,
  mentionTypeName: TMentionElement['mentionTypeName']
): boolean {
  const nodes = deserializeMarkdownWithMentions(editor, markdown);
  return nodes.some(node => getDescendantHasMentionType(node, mentionTypeName));
}

export function getMarkdownUserMentionIds(
  editor: EditorWithMarkdownApi,
  markdown: string
): string[] {
  const nodes = deserializeMarkdownWithMentions(editor, markdown);
  return [...new Set(nodes.flatMap(collectUserMentionIds))];
}

export function getMarkdownMentionEmails(
  editor: EditorWithMarkdownApi,
  markdown: string
): string[] {
  const nodes = deserializeMarkdownWithMentions(editor, markdown);
  return [...new Set(nodes.flatMap(collectEmailMentionEmails))];
}

function getDescendantHasMentionType(
  node: Descendant,
  mentionTypeName: TMentionElement['mentionTypeName']
): boolean {
  if (isMentionElement(node)) return node.mentionTypeName === mentionTypeName;
  if (!ElementApi.isElement(node)) return false;
  return node.children.some(child => getDescendantHasMentionType(child, mentionTypeName));
}

function collectUserMentionIds(node: Descendant): string[] {
  if (isMentionElement(node) && node.mentionTypeName === 'user') return [node.userId];
  if (!ElementApi.isElement(node)) return [];

  return node.children.flatMap(child => collectUserMentionIds(child));
}

function collectEmailMentionEmails(node: Descendant): string[] {
  if (isMentionElement(node) && node.mentionTypeName === 'email') return [node.email];
  if (!ElementApi.isElement(node)) return [];

  return node.children.flatMap(child => collectEmailMentionEmails(child));
}
